<template>
  <div data-slot="page-header" :class="rootClasses">
    <teleport defer to="#app-header-title" :disabled="!inShell">
      <span v-if="title" :class="[ 'flex min-w-0 items-center gap-2', !inShell && 'w-full pt-4 text-lg font-semibold' ]">
        <slot name="title-prepend" />
        <span class="truncate">{{ title }}</span>
        <new-badge v-if="badge">
          {{ badge }}
        </new-badge>
        <info-popup v-if="info">
          {{ info }}
        </info-popup>
      </span>
    </teleport>

    <teleport
      v-if="back"
      defer
      to="#app-header-start"
      :disabled="!inShell"
    >
      <router-link :to="back" class="flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-foreground no-underline transition-colors hover:bg-accent">
        <arrow-left-icon class="size-4 shrink-0" />
        <span class="truncate max-md:hidden">{{ backLabel || tr('Back') }}</span>
      </router-link>
    </teleport>

    <teleport
      v-if="$slots.start"
      defer
      to="#app-header-start"
      :disabled="!actionsInHeader"
    >
      <slot name="start" />
    </teleport>

    <!-- On phones the actions stay on the page: the header bar is too narrow. -->
    <teleport defer to="#app-header-actions" :disabled="!actionsInHeader">
      <div v-if="$slots.buttons" class="flex items-center gap-2 max-md:w-full max-md:flex-wrap max-md:[&>*]:flex-1">
        <slot name="buttons" />
      </div>
    </teleport>

    <div v-if="$slots.description" class="text-center text-sm text-muted-foreground">
      <slot name="description" />
    </div>

    <!-- Tabs go to the second row of the header bar, as in Creator. -->
    <teleport defer to="#app-header-bottom" :disabled="!inShell">
      <slot />
    </teleport>
  </div>
</template>

<script>
import { ArrowLeft } from '@lucide/vue';

import InfoPopup from '@/ui/components/InfoPopup.vue';
import NewBadge from '@/ui/components/NewBadge.vue';

const HEADER_ACTIONS_MIN_WIDTH = 768;

/**
 * Page title, actions and tabs (default slot) in the app shell's header bar
 * (title centered, actions right, tabs in a second row, as in Creator), plus
 * an optional description at the top of the page. Outside the app shell
 * (public report) everything is rendered in place.
 */
export default {
  name: 'PageHeader',
  components: {
    ArrowLeftIcon: ArrowLeft,
    InfoPopup,
    NewBadge,
  },
  inject: {
    hasShellHeader: {
      default: () => () => false,
    },
  },
  props: {
    title: {
      type: String,
      required: false,
      default: () => '',
    },
    // Small pill next to the title, e.g. "Lite".
    badge: {
      type: String,
      required: false,
      default: () => '',
    },
    info: {
      type: String,
      required: false,
      default: () => '',
    },
    // Route of the "Back" link on the left of the header bar.
    back: {
      type: String,
      required: false,
      default: () => '',
    },
    backLabel: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  computed: {
    inShell() {
      return this.hasShellHeader();
    },
    actionsInHeader() {
      return this.inShell && this.$root.screenWidth >= HEADER_ACTIONS_MIN_WIDTH;
    },
    rootClasses() {
      if (!this.hasPageContent) {
        return 'contents';
      }

      return 'flex flex-col items-center gap-3 pt-2 pb-5';
    },
    hasPageContent() {
      return !!(this.$slots.description || !this.inShell || ((this.$slots.buttons || this.$slots.start) && !this.actionsInHeader));
    },
  },
};
</script>
